import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Container } from "react-bootstrap";
import { FiArrowRight, FiFileText, FiSearch, FiX } from "react-icons/fi";
import { products, productCategories, isProductInCategory } from "../../data/products";
import ContactModal from "../ContactModal/ContactModal";
import Seo, { breadcrumbSchema, webPageSchema } from "../Seo/Seo";
import ProductCard from "./ProductCard";
import MobileCategorySelect from "./MobileCategorySelect";
import { matchesProductSearch } from "../../utils/productDiscovery";
import { productListSchema } from "../Seo/productSeo";

export const CATALOG_PATH = "/catalogs/klean-leaf-catalog.pdf";

function Products() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [hydrated, setHydrated] = useState(false);
  const [pageSize, setPageSize] = useState(15);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const resultsRef = useRef(null);
  // Match the static catalog first, then apply URL filters after hydration.
  useEffect(() => setHydrated(true), []);
  useEffect(() => {
    if (typeof window.matchMedia !== "function") return undefined;
    const media = window.matchMedia("(max-width: 991px)");
    const updatePageSize = () => setPageSize(media.matches ? 12 : 15);
    updatePageSize();
    media.addEventListener("change", updatePageSize);
    return () => media.removeEventListener("change", updatePageSize);
  }, []);
  const categoryParam = hydrated ? searchParams.get("category") : null;
  const category = productCategories.find((item) => item.id === categoryParam);
  const query = hydrated ? searchParams.get("q") || "" : "";
  const filteredProducts = products.filter((product) => (
    (!category || isProductInCategory(product, category.id))
    && matchesProductSearch(product, query)
  ));
  const totalPages = Math.max(1, Math.ceil(filteredProducts.length / pageSize));
  const pageParam = hydrated ? searchParams.get("page") : null;
  const requestedPage = Number(pageParam);
  const currentPage = Math.min(totalPages, Number.isSafeInteger(requestedPage) && requestedPage > 0 ? requestedPage : 1);
  const startIndex = (currentPage - 1) * pageSize;
  const visibleProducts = filteredProducts.slice(startIndex, startIndex + pageSize);

  // Keep bookmarked, resized, and out-of-range pages aligned with the visible results.
  useEffect(() => {
    if (!hydrated || !pageParam || pageParam === String(currentPage)) return;
    const next = new URLSearchParams(searchParams);
    if (currentPage > 1) next.set("page", String(currentPage));
    else next.delete("page");
    setSearchParams(next, { replace: true });
  }, [hydrated, pageParam, currentPage, searchParams, setSearchParams]);

  const showResults = () => {
    if (resultsRef.current?.getBoundingClientRect().top < 112) {
      resultsRef.current.scrollIntoView({ block: "start" });
    }
  };
  const updateFilters = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    // Category choices open the full collection advertised by their counts.
    if (key === "category") next.delete("q");
    next.delete("page");
    setSearchParams(next, { replace: key === "q" });
    if (key === "category") showResults();
  };
  const changePage = (page) => {
    const next = new URLSearchParams(searchParams);
    if (page > 1) next.set("page", String(page));
    else next.delete("page");
    setSearchParams(next);
    showResults();
  };
  const resetFilters = () => setSearchParams({});
  const categories = [{ id: "", name: "All Products", count: products.length }, ...productCategories]
    .map((item) => item.id === "fragrance-booster-beads" ? { ...item, name: "Fragrance Beads" } : item);
  const description = "Explore Klean Leaf’s private label laundry detergent sheets, our core manufacturing expertise, plus tablets, pods, powders and home care products.";

  return (
    <>
      <Seo
        title="Laundry Detergent Sheets & Private Label Products | Klean Leaf"
        description={description}
        path="/products"
        image="/logo.webp"
        imageAlt="Klean Leaf logo"
        schema={[
          webPageSchema({ name: "Laundry Detergent Sheets & Private Label Products", description, path: "/products", type: "CollectionPage", primaryImage: "/logo.webp" }),
          breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Products", path: "/products" }]),
          productListSchema(products),
        ]}
      />
      <main className="products-page mobile-type" id="product-catalog">
        <section className="catalog-hero">
          <Container>
            <p className="products-eyebrow">Laundry Sheets First</p>
            <div className="catalog-hero-layout">
              <div>
                <h1>Laundry Detergent Sheets &amp; More</h1>
                <p><Link className="product-context-link" to="/products/laundry-detergent-sheets">Laundry detergent sheets</Link> are our core manufacturing expertise. Explore private label options, then expand into tablets, pods, cleaning sheets, powders, bottles and kits.</p>
              </div>
              <div className="products-actions">
                <button type="button" className="products-btn products-btn-primary" onClick={() => setIsContactModalOpen(true)}>
                  Request a Quote <FiArrowRight aria-hidden="true" />
                </button>
                <a className="catalog-pdf-link" href={CATALOG_PATH} target="_blank" rel="noopener noreferrer">
                  <FiFileText aria-hidden="true" /> View Catalog PDF <span className="visually-hidden">(opens in a new tab)</span>
                </a>
              </div>
            </div>
          </Container>
        </section>

        <Container className="catalog-layout">
          <aside className="catalog-sidebar" aria-label="Product categories">
            <div className="catalog-category-desktop">
              <h2>Browse by category</h2>
              <ul>
                {categories.map((item) => (
                  <li key={item.id}>
                    <button type="button"
                      className={(!category ? !item.id : item.id === category.id) ? "is-selected" : ""}
                      aria-pressed={!category ? !item.id : item.id === category.id}
                      onClick={() => updateFilters("category", item.id)}>
                      <span>{item.name}</span><span className="category-count">{item.count}</span>
                    </button>
                  </li>
                ))}
              </ul>
              <div className="catalog-help">
                <h3>Let’s discuss your project.</h3>
                <p>Start with custom laundry sheets or plan your next home care product. We’ll help clarify specifications and private label options.</p>
                <button type="button" onClick={() => setIsContactModalOpen(true)}>Contact our team <FiArrowRight aria-hidden="true" /></button>
              </div>
            </div>
            <MobileCategorySelect categories={categories} value={category?.id || ""}
              onChange={(value) => updateFilters("category", value)} />
          </aside>

          <section ref={resultsRef} className="catalog-results" aria-labelledby="catalog-results-heading">
            <div className="catalog-toolbar">
              <div className="catalog-results-title">
                <h2 id="catalog-results-heading">{category?.name || "All Products"}</h2>
                <p role="status" aria-live="polite" aria-atomic="true">
                  {filteredProducts.length ? `Showing ${startIndex + 1}–${startIndex + visibleProducts.length} of ${filteredProducts.length} ${filteredProducts.length === 1 ? "product" : "products"}` : "0 products found"}
                </p>
              </div>
              <div className="catalog-search" role="search">
                <label htmlFor="product-search">Search products</label>
                <div className="catalog-search-input">
                  <FiSearch aria-hidden="true" />
                  <input id="product-search" type="search" placeholder="Search by name, SKU or category" value={query} onChange={(event) => updateFilters("q", event.target.value)} />
                  {query && <button type="button" aria-label="Clear search" onClick={() => updateFilters("q", "")}><FiX aria-hidden="true" /></button>}
                </div>
              </div>
            </div>
            {(category || query) && (
              <div className="catalog-filter-summary">
                <span>{category?.name || "All categories"}{query.trim() && <> · Search: “{query.trim()}”</>}</span>
                <button type="button" onClick={resetFilters}>Clear all filters</button>
              </div>
            )}
            {filteredProducts.length > 0 ? (
              <>
                <div className="catalog-grid">
                  {visibleProducts.map((product) => <ProductCard key={product.slug} product={product} />)}
                </div>
                {totalPages > 1 && (
                  <nav className="catalog-pagination" aria-label="Product pagination">
                    <button type="button" aria-label="Previous page" disabled={currentPage === 1} onClick={() => changePage(currentPage - 1)}>
                      <FiArrowRight className="pagination-previous-icon" aria-hidden="true" /><span>Previous</span>
                    </button>
                    <div className="catalog-page-numbers">
                      {Array.from({ length: totalPages }, (_, index) => index + 1).map((page) => (
                        <button key={page} type="button" aria-label={`Go to page ${page}`} aria-current={currentPage === page ? "page" : undefined} onClick={() => changePage(page)}>{page}</button>
                      ))}
                    </div>
                    <button type="button" aria-label="Next page" disabled={currentPage === totalPages} onClick={() => changePage(currentPage + 1)}>
                      <span>Next</span><FiArrowRight aria-hidden="true" />
                    </button>
                  </nav>
                )}
              </>
            ) : (
              <div className="catalog-empty">
                <FiSearch aria-hidden="true" />
                <h3>No products found</h3>
                <p>Try another product name, SKU or category, or clear a filter.</p>
                <button type="button" className="products-btn products-btn-primary" onClick={resetFilters}>Show all {products.length} products</button>
              </div>
            )}
          </section>
        </Container>
        <section className="catalog-next-steps" aria-labelledby="catalog-project-questions">
          <Container className="catalog-inquiry-layout">
            <div className="catalog-faq">
              <h2 id="catalog-project-questions">Private label project questions</h2>
              <details>
                <summary>Can I develop products under my own brand?</summary>
                <p>Our core expertise is private label laundry detergent sheets, with manufacturing capabilities across the wider catalog. Branding, packaging and formulation options are reviewed and confirmed for your selected product.</p>
              </details>
              <details>
                <summary>What information should I include in my inquiry?</summary>
                <p>Share the product you’re interested in, your target market, estimated order quantity, and packaging preferences. Let us know if you need help defining any of these requirements.</p>
              </details>
              <details>
                <summary>What are the minimum order quantity and production lead time?</summary>
                <p>Minimum order quantities and lead times depend on the product, specifications, packaging, and order plan. Our team will confirm these details for your project during the quotation process.</p>
              </details>
              <details className="catalog-directory">
                <summary>Browse the complete product directory</summary>
                {productCategories.map((item) => (
                  <div key={item.id}>
                    <h3>{item.name}</h3>
                    <ul>{products.filter((product) => product.category === item.id).map((product) => (
                      <li key={product.slug}><Link to={`/products/${product.slug}`}>{product.name}</Link></li>
                    ))}</ul>
                  </div>
                ))}
              </details>
            </div>
            <div className="catalog-project-cta">
              <h2>Plan your private label project</h2>
              <p>Build your laundry sheet range or expand into complementary home care products. Discuss formula, packaging and sample requirements with our team.</p>
              <button type="button" className="products-btn products-btn-primary" onClick={() => setIsContactModalOpen(true)}>
                Request a Private Label Quote <FiArrowRight aria-hidden="true" />
              </button>
              <div className="catalog-project-links">
                <Link to="/solution">Explore Laundry Sheet Solutions <FiArrowRight aria-hidden="true" /></Link>
                <Link to="/production">View Laundry Sheet Manufacturing <FiArrowRight aria-hidden="true" /></Link>
              </div>
            </div>
          </Container>
        </section>
      </main>
      <ContactModal mobileTypography isModalOpen={isContactModalOpen} handleCancel={() => setIsContactModalOpen(false)} idPrefix="products-contact" title="Request a Private Label Quote" />
    </>
  );
}

export default Products;
